'use client';
import { ExpectedLoyaltyPoints } from '@eliadmin/core/src/components/loyalty/expected-loyalty-points';

import {
  CUSTOMER_CREDIT_TYPES,
  getCustomerCreditBalance,
} from '@eliadmin/core/src/api/customers/customer-credit-utils';
import { ACCOUNT_COPY } from '@eliadmin/core/src/components/dlz/account/account-copy';
import {
  ACCOUNT_PAGES,
  useAccountPage,
} from '@eliadmin/core/src/components/dlz/account/account-pages';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const ITEM =
  'flex w-full items-center gap-3 rounded-[18px] p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F1012]';
/** The nav icons are drawn 1.8 px (the shared set 1.5); the heart keeps 1.5. */
const NAV_STROKE = '[&_[stroke]]:[stroke-width:1.8]';

/** Figma „Loyalty“ (60321:19084): points and their value, only with the store's loyalty program. */
function LoyaltyBalance() {
  const loyaltyProgram = useGlobalStore(s => s.generalLayout.loyalty_program);
  const { currency, currency_settings: currencySettings } = useGlobalStore(s => s.generalLayout);
  const customer = useCustomerStore(s => s.me?.customer);
  if (!loyaltyProgram) return <ExpectedLoyaltyPoints />;

  const points = Number(
    getCustomerCreditBalance(customer, CUSTOMER_CREDIT_TYPES.LOYALTY_POINTS) ?? 0,
  );
  const safePoints = Number.isFinite(points) ? points : 0;
  const value = safePoints * Number(loyaltyProgram.point_to_currency ?? 0);

  return (
    <div className="flex w-full items-center gap-2.5 rounded-[18px] bg-[#F5F6F7] px-3.5 py-3">
      <Icon name="star" size={18} className="text-[#E89A0C] [&_[stroke]]:[stroke-width:2]" />
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="dlz-body-s-medium text-[#0F1012]">
          {ACCOUNT_COPY.nav.loyaltyPoints(new Intl.NumberFormat('sk').format(safePoints))}
        </p>
        <p className="dlz-caption text-[#6B7079]">
          {ACCOUNT_COPY.nav.loyaltyValue(formatPrice(value, currency, currencySettings))}
        </p>
        <ExpectedLoyaltyPoints />
      </div>
    </div>
  );
}

/**
 * Figma „Account nav“ (60317:24489): name and e-mail, the loyalty balance, the four pages (the
 * current one on bg/surface in Bold text/brand with a brand icon) and „Odhlásiť sa“ under a divider.
 * `framed` – the 427 px card beside the content; without it (the phone menu) only the content.
 */
export function AccountNav({
  framed = true,
  onNavigate,
}: {
  framed?: boolean;
  onNavigate?: () => void;
}) {
  const customer = useCustomerStore(s => s.me?.customer);
  const current = useAccountPage();

  return (
    <nav
      aria-label={ACCOUNT_COPY.nav.label}
      className={cn(
        'flex flex-col gap-4',
        framed && 'rounded-[18px] border border-[#E2E4E7] bg-white p-6',
      )}
    >
      {customer ? (
        <div className="flex flex-col items-center gap-0.5 text-center">
          <p className="dlz-h5 w-full truncate text-[#0F1012]">
            {[customer.name, customer.surname].filter(Boolean).join(' ')}
          </p>
          <p className="dlz-caption w-full truncate text-[#6B7079]">{customer.email}</p>
        </div>
      ) : null}
      <LoyaltyBalance />
      <ul className="flex flex-col">
        {ACCOUNT_PAGES.map(page => {
          const active = current?.key === page.key;
          return (
            <li key={page.key}>
              <Link
                href={page.href()}
                prefetch={false}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  ITEM,
                  active ? 'bg-[#F5F6F7] text-[#D7191F]' : 'text-[#0F1012] hover:bg-[#F5F6F7]',
                )}
              >
                <Icon
                  name={page.icon}
                  size={24}
                  className={cn(page.icon !== 'heart' && NAV_STROKE)}
                />
                <span className={cn('dlz-body-m min-w-0 flex-1', active && 'font-bold')}>
                  {page.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      <hr className="border-[#E2E4E7]" />
      {/* A plain link: the sign-out route clears the session cookie and redirects. */}
      <a href={ROUTES.SIGN_OUT()} className={cn(ITEM, 'px-3.5 text-[#4F545C] hover:bg-[#F5F6F7]')}>
        <Icon name="arrow-circle-left" size={24} className={cn('text-[#6B7079]', NAV_STROKE)} />
        <span className="dlz-body-m min-w-0 flex-1">{ACCOUNT_COPY.nav.signOut}</span>
      </a>
    </nav>
  );
}
